<template>
  <div ref="searchInputTarget">
    <div
      class="relative w-full overflow-visible transition-all transform divide-y"
      :class="{ 'z-50': isDropdownOpen }"
      @mouseenter="showTooltip"
      @mouseleave="hideTooltip">
      <div class="relative">
        <svg
          class="absolute text-gray-400 pointer-events-none left-3.5 z-20"
          :class="size === 'sm' ? 'top-2 w-4 h-4' : 'top-2.5 w-5 h-5'"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true">
          <path
            fill-rule="evenodd"
            d="M9 3.5a5.5 5.5 0 100 11 5.5 5.5 0 000-11zM2 9a7 7 0 1112.452 4.391l3.328 3.329a.75.75 0 11-1.06 1.06l-3.329-3.328A7 7 0 012 9z"
            clip-rule="evenodd" />
        </svg>
        <div
          v-if="!isSearching && selectedItem"
          @click="!disabled && startSearching()"
          :class="[inputClass, disabled ? 'cursor-default' : 'cursor-pointer', 'flex items-center']"
          role="combobox"
          aria-expanded="false"
          aria-controls="options">
          <span class="block truncate">{{ selectedItem.name }}</span>
        </div>
        <input
          v-else
          type="text"
          ref="searchInputRef"
          v-model="searchInput"
          @input="debouncedUpdateSearch"
          @click="toggleDropdown"
          @blur="handleInputBlur"
          autocomplete="off"
          :disabled="disabled"
          :class="inputClass"
          :placeholder="
            selectedItem
              ? selectedItem.name
              : placeholder
                ? placeholder
                : $t('common.actions.search')
          "
          role="combobox"
          aria-expanded="false"
          aria-controls="options" />
        <IconXmark
          class="absolute text-gray-400 right-3.5 z-30"
          :class="[
            { 'hidden': (!searchInput && !selectedItem) || disabled },
            disabled ? 'cursor-default' : 'cursor-pointer',
            size === 'sm' ? 'top-2 w-4 h-4' : 'top-2.5 w-5 h-5',
          ]"
          @click="!disabled && onClearSelection()" />
      </div>
      <Teleport to="body">
        <Transition
          enter-active-class="duration-300 ease-out"
          enter-from-class="scale-95 opacity-0"
          enter-to-class="scale-100 opacity-100"
          leave-active-class="duration-200 ease-in"
          leave-from-class="scale-100 opacity-100"
          leave-to-class="scale-95 opacity-0">
          <div
            v-if="isDropdownOpen"
            ref="dropdownRef"
            :style="{
              position: strategy,
              top: y ? `${y}px` : '',
              left: x ? `${x}px` : '',
              zIndex: 9999,
            }"
            class="mt-1 bg-white shadow-xl">
            <div v-if="displayedItems && displayedItems.length">
              <ul
                ref="listElement"
                class="py-2 overflow-y-auto text-sm text-gray-800 max-h-48 scroll-py-2"
                id="options"
                role="listbox">
                <li
                  v-for="item in displayedItems"
                  :key="item.value"
                  @click="itemSelected(item)"
                  class="px-4 py-2 cursor-default select-none hover:bg-cyan-light"
                  id="option-5"
                  role="option"
                  tabindex="-1">
                  {{ item.name }}
                </li>
              </ul>
              <p class="w-full p-2 text-xs text-right text-gray-500">
                {{ displayedItems.length }} of {{ displayCount }}
              </p>
            </div>
            <p v-else class="p-4 text-sm text-gray-500">
              {{ noResultsText }}
            </p>
          </div>
        </Transition>
      </Teleport>
    </div>
    <p v-if="error && !showErrorAsTooltip" class="mt-1 text-xs text-red-500">{{ error }}</p>
    <p v-if="!error && warning && !showErrorAsTooltip" class="mt-1 text-xs text-orange-500">
      {{ warning }}
    </p>
    <Teleport to="body">
      <div
        v-if="!!showErrorAsTooltip && tooltipVisible && error"
        ref="tooltipRef"
        :style="{
          position: tStrategy,
          top: ty ? `${ty}px` : '',
          left: tx ? `${tx}px` : '',
          zIndex: 9999,
        }"
        class="relative flex flex-col items-center px-3 py-2 text-white bg-red-500 rounded-lg shadow-lg">
        <span class="text-xs truncate">{{ error }}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="absolute w-4 h-4 text-red-500 fill-current"
          :style="{ bottom: '-8px', left: 'calc(50% - 8px)', transform: 'rotate(45deg)' }"
          viewBox="0 0 24 24">
          <path d="M20 3H4a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1z"></path>
        </svg>
      </div>
      <div
        v-if="!!showErrorAsTooltip && tooltipVisible && !error && warning"
        ref="tooltipRef"
        :style="{
          position: tStrategy,
          top: ty ? `${ty}px` : '',
          left: tx ? `${tx}px` : '',
          zIndex: 9999,
        }"
        class="relative flex flex-col items-center px-3 py-2 text-white bg-orange-500 rounded-lg shadow-lg">
        <span class="text-xs truncate">{{ warning }}</span>
        <svg
          xmlns="http://www.w3.org/2000/svg"
          class="absolute w-4 h-4 text-orange-500 fill-current"
          :style="{ bottom: '-8px', left: 'calc(50% - 8px)', transform: 'rotate(45deg)' }"
          viewBox="0 0 24 24">
          <path d="M20 3H4a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1z"></path>
        </svg>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch, watchEffect } from 'vue';
import { cva } from 'class-variance-authority';
import { onClickOutside, useInfiniteScroll, useDebounceFn } from '@vueuse/core';
import { useFloating, offset, flip, shift, autoUpdate, hide } from '@floating-ui/vue';
import IconXmark from '@/assets/icons/IconXmark.vue';
import type { SelectDropdownItem } from '@/types/Base';

const selectedItem = defineModel<SelectDropdownItem | null>();
const searchInput = ref('');
const isDropdownOpen = ref(false);
const isSearching = ref(false);
const emit = defineEmits(['updateSearch', 'itemSelected', 'clearSelection']);
const listElement = ref<HTMLElement | null>(null);
const page = ref(1);
let scrollTop = 0;
let listUpdated = false;

const searchInputTarget = ref(null);
onClickOutside(searchInputTarget, () => {
  isDropdownOpen.value = false;
  isSearching.value = false;
});

type Props = {
  listItems: SelectDropdownItem[] | undefined;
  noResultsText: string;
  count?: number;
  error?: string;
  warning?: string;
  showErrorAsTooltip?: boolean;
  size?: 'sm' | 'default';
  placeholder?: string;
  disabled?: boolean;
  internalFilter?: boolean;
};
const props = withDefaults(defineProps<Props>(), {
  noResultsText: 'No Results',
  size: 'default',
  showErrorAsTooltip: false,
  internalFilter: false,
});

const displayedItems = computed(() => {
  if (!props.internalFilter || !props.listItems) return props.listItems;
  if (!searchInput.value.trim()) return props.listItems;
  const query = searchInput.value.toLowerCase();
  return props.listItems.filter(
    (item) =>
      item.name.toLowerCase().includes(query) || item.displayName?.toLowerCase().includes(query)
  );
});

const displayCount = computed(() => {
  return props.internalFilter ? props.listItems?.length ?? 0 : props.count ?? 0;
});

const inputClass = computed(() => {
  return cva(
    'w-full rounded-lg pl-10 pr-10 text-sm text-gray-900 ring-1 ring-inset shadow-sm focus:outline-none focus:ring-2 focus:ring-theme-500',
    {
      variants: {
        size: {
          default: 'py-2.5 h-10',
          sm: 'py-1 h-8',
        },
        error: {
          true: 'ring-red-500',
          false: 'ring-gray-300',
        },
        warning: {
          true: 'ring-blue-800',
          false: 'ring-gray-300',
        },
        open: {
          true: 'rounded-t-lg',
          false: 'rounded-lg',
        },
        disabled: {
          true: 'bg-opacity-75 pointer-events-none text-gray-500 bg-gray-100 border border-gray-300',
          false: '',
        },
      },
    }
  )({
    size: props.size,
    error: !!props.error,
    warning: !props.error && !!props.warning,
    open: isDropdownOpen.value,
    disabled: props.disabled,
  });
});

watch(
  () => selectedItem.value,
  () => {
    if (selectedItem?.value) searchInput.value = selectedItem.value.name;
    else searchInput.value = '';
  },
  { deep: true, immediate: true }
);

watch(
  () => props.listItems,
  async (newList, oldList) => {
    listUpdated = true;
    if (newList && oldList && newList.length > oldList.length && listElement.value) {
      listElement.value.scrollTop = scrollTop;
    }
  }
);

function itemSelected(item: SelectDropdownItem) {
  isDropdownOpen.value = false;
  isSearching.value = false;
  emit('itemSelected', item);
}

const debouncedUpdateSearch = useDebounceFn(
  (clear = false) => {
    if (!clear && !isDropdownOpen.value) isDropdownOpen.value = true;
    page.value = 1;
    if (!props.internalFilter) emit('updateSearch', searchInput.value, page.value);
    listUpdated = false;
    if (!isDropdownOpen.value) isDropdownOpen.value = true;
  },
  500,
  { maxWait: 800 }
);

const refreshThreshold = 100;
useInfiniteScroll(
  listElement,
  () => {
    if (listUpdated && listElement.value && listElement.value.scrollHeight > refreshThreshold) {
      scrollTop = listElement?.value?.scrollTop ?? 0;
      if (!props.internalFilter) emit('updateSearch', searchInput.value, page.value);
      page.value += 1;
      listUpdated = false;
    }
  },
  { distance: refreshThreshold }
);

function onClearSelection() {
  searchInput.value = '';
  selectedItem.value = null;
  isSearching.value = false;
  debouncedUpdateSearch(true);
  emit('clearSelection');
}

const searchInputRef = ref<HTMLElement | null>(null);
const dropdownRef = ref<HTMLElement | null>(null);
const tooltipRef = ref<HTMLElement | null>(null);
const tooltipVisible = ref(false);

const { x, y, strategy, middlewareData, update } = useFloating(searchInputRef, dropdownRef, {
  placement: 'bottom-start',
  middleware: [offset(5), flip(), shift(), hide()],
  whileElementsMounted: (referenceEl, floatingEl, update) => {
    return autoUpdate(referenceEl, floatingEl, update);
  },
});

const {
  x: tx,
  y: ty,
  strategy: tStrategy,
  update: tUpdate,
  middlewareData: tMiddlewareData,
} = useFloating(searchInputTarget, tooltipRef, {
  placement: 'top',
  middleware: [offset(5), flip(), shift(), hide()],
  whileElementsMounted: (referenceEl, floatingEl, tUpdate) => {
    return autoUpdate(referenceEl, floatingEl, tUpdate);
  },
});

function showTooltip() {
  tooltipVisible.value = true;
  tUpdate();
}

function hideTooltip() {
  tooltipVisible.value = false;
}

watchEffect(() => {
  if (tMiddlewareData.value.hide?.referenceHidden) {
    tooltipVisible.value = false;
  }
});

watchEffect(() => {
  if (middlewareData.value.hide?.referenceHidden) {
    isDropdownOpen.value = false;
  }
});

function toggleDropdown() {
  if (props.disabled) return;
  isDropdownOpen.value = !isDropdownOpen.value;
  if (isDropdownOpen.value) {
    update();
  }
}

function startSearching() {
  if (props.disabled) return;
  isSearching.value = true;
  isDropdownOpen.value = true;
  searchInput.value = '';
  // Focus the input after Vue updates the DOM
  setTimeout(() => {
    searchInputRef.value?.focus();
  }, 0);
}

function handleInputBlur() {
  // Small delay to allow item selection to complete
  setTimeout(() => {
    if (!isDropdownOpen.value) {
      isSearching.value = false;
    }
  }, 150);
}
</script>
